/**
 * Family Tree Validation Rules
 *
 * Individual validation rules that protect the family graph from invalid mutations.
 * Each rule is a pure function that checks one specific constraint.
 *
 * Rules are composable and can be combined to create comprehensive validation logic.
 */

import type {
  ValidationViolation,
  FamilyTreeMutation,
  FamilyTreeGraphContext,
  ProductRules,
} from './types';
import * as GraphInspector from './graphInspector';

// ==================== RULE 1: SELF-RELATIONSHIP PREVENTION ====================

/**
 * Rule: A person cannot be linked to themselves
 *
 * Prevents mutations where sourcePersonId === targetPersonId
 * Applies to all relationship types (spouse, parent, child)
 */
export function validateNoSelfRelationship(
  mutation: FamilyTreeMutation,
  context: FamilyTreeGraphContext,
  rules: ProductRules
): ValidationViolation | null {
  // Allow self-relationships if explicitly enabled (unusual but configurable)
  if (rules.allowSelfRelationships) {
    return null;
  }

  if (mutation.sourcePersonId === mutation.targetPersonId) {
    return {
      code: 'SELF_RELATIONSHIP',
      severity: 'error',
      message: 'A person cannot be related to themselves.',
      details: {
        personId: mutation.sourcePersonId,
        mutationType: mutation.type,
      },
    };
  }

  return null;
}

// ==================== RULE 2: DUPLICATE SPOUSE PREVENTION ====================

/**
 * Rule: Do not create the same spouse relationship twice
 *
 * Checks if a spouse relationship already exists between two persons
 * Applies only to 'add_spouse' mutations
 */
export function validateNoDuplicateSpouse(
  mutation: FamilyTreeMutation,
  context: FamilyTreeGraphContext,
  rules: ProductRules
): ValidationViolation | null {
  if (mutation.type !== 'add_spouse') {
    return null; // Rule doesn't apply to non-spouse mutations
  }

  const exists = GraphInspector.spouseRelationshipExists(
    context,
    mutation.sourcePersonId,
    mutation.targetPersonId
  );

  if (exists) {
    const sourcePerson = GraphInspector.getPerson(context, mutation.sourcePersonId);
    const targetPerson = GraphInspector.getPerson(context, mutation.targetPersonId);

    return {
      code: 'DUPLICATE_SPOUSE',
      severity: 'error',
      message: `${sourcePerson?.first_name || 'This person'} is already married to ${targetPerson?.first_name || 'this person'}.`,
      details: {
        sourcePersonId: mutation.sourcePersonId,
        targetPersonId: mutation.targetPersonId,
      },
    };
  }

  return null;
}

// ==================== RULE 3: DUPLICATE PARENT-CHILD PREVENTION ====================

/**
 * Rule: Do not create the same parent-child relationship twice
 *
 * Checks if a parent-child relationship already exists
 * Applies to both 'add_parent' and 'add_child' mutations
 */
export function validateNoDuplicateParentChild(
  mutation: FamilyTreeMutation,
  context: FamilyTreeGraphContext,
  rules: ProductRules
): ValidationViolation | null {
  let parentId: string;
  let childId: string;

  if (mutation.type === 'add_parent') {
    parentId = mutation.targetPersonId;
    childId = mutation.sourcePersonId;
  } else if (mutation.type === 'add_child') {
    parentId = mutation.sourcePersonId;
    childId = mutation.targetPersonId;
  } else {
    return null; // Rule doesn't apply to spouse mutations
  }

  const exists = GraphInspector.parentChildRelationshipExists(context, parentId, childId);

  if (exists) {
    const parent = GraphInspector.getPerson(context, parentId);
    const child = GraphInspector.getPerson(context, childId);

    return {
      code: 'DUPLICATE_PARENT_CHILD',
      severity: 'error',
      message: `${parent?.first_name || 'This person'} is already a parent of ${child?.first_name || 'this person'}.`,
      details: {
        parentId,
        childId,
      },
    };
  }

  return null;
}

// ==================== RULE 4: INVALID PARENT COUNT PREVENTION ====================

/**
 * Rule: Prevent impossible parent assignment scenarios
 *
 * Enforces product rules for maximum parents per person
 * Prevents adding more parents than allowed
 * Applies only to 'add_parent' mutations
 */
export function validateParentCount(
  mutation: FamilyTreeMutation,
  context: FamilyTreeGraphContext,
  rules: ProductRules
): ValidationViolation | null {
  if (mutation.type !== 'add_parent') {
    return null; // Rule only applies to add_parent mutations
  }

  const childId = mutation.sourcePersonId;
  const currentParentCount = GraphInspector.countParents(context, childId);

  // Check if adding another parent would exceed the limit
  if (currentParentCount >= rules.maxParentsPerPerson) {
    const child = GraphInspector.getPerson(context, childId);

    return {
      code: 'INVALID_PARENT_COUNT',
      severity: 'error',
      message: `${child?.first_name || 'This person'} already has the maximum number of parents (${rules.maxParentsPerPerson}).`,
      details: {
        childId,
        currentParentCount,
        maxParentsAllowed: rules.maxParentsPerPerson,
      },
    };
  }

  // Check for gender-specific parent constraints (optional)
  if (mutation.parentGender) {
    const hasParentOfGender = GraphInspector.hasParentOfGender(
      context,
      childId,
      mutation.parentGender
    );

    if (hasParentOfGender) {
      const child = GraphInspector.getPerson(context, childId);
      const genderLabel = mutation.parentGender === 'male' ? 'father' : 'mother';

      return {
        code: 'INVALID_PARENT_COUNT',
        severity: 'error',
        message: `${child?.first_name || 'This person'} already has a ${genderLabel}.`,
        details: {
          childId,
          parentGender: mutation.parentGender,
        },
      };
    }
  }

  return null;
}

// ==================== RULE 5: ANCESTRY CYCLE PREVENTION ====================

/**
 * Rule: Prevent creating loops in the family graph
 *
 * Ensures that adding a parent-child relationship won't create a cycle
 * where a person becomes their own ancestor
 *
 * Example of invalid cycle:
 * - Person A is parent of Person B
 * - Person B is parent of Person C
 * - Trying to make Person C parent of Person A (creates cycle)
 *
 * Applies to both 'add_parent' and 'add_child' mutations
 */
export function validateNoAncestryCycle(
  mutation: FamilyTreeMutation,
  context: FamilyTreeGraphContext,
  rules: ProductRules
): ValidationViolation | null {
  let parentId: string;
  let childId: string;

  if (mutation.type === 'add_parent') {
    parentId = mutation.targetPersonId;
    childId = mutation.sourcePersonId;
  } else if (mutation.type === 'add_child') {
    parentId = mutation.sourcePersonId;
    childId = mutation.targetPersonId;
  } else {
    return null; // Rule doesn't apply to spouse mutations
  }

  const wouldCreateCycle = GraphInspector.wouldCreateCycle(context, parentId, childId);

  if (wouldCreateCycle) {
    const parent = GraphInspector.getPerson(context, parentId);
    const child = GraphInspector.getPerson(context, childId);

    return {
      code: 'ANCESTRY_CYCLE',
      severity: 'error',
      message: `Cannot add this relationship: ${parent?.first_name || 'This person'} is already a descendant of ${child?.first_name || 'this person'}. This would create an impossible family loop.`,
      details: {
        parentId,
        childId,
        explanation: 'Adding this parent-child relationship would make a person their own ancestor.',
      },
    };
  }

  return null;
}

// ==================== RULE 6: INVALID RELATIONSHIP DIRECTION PROTECTION ====================

/**
 * Rule: Parent/child semantics must stay correct
 *
 * Prevents relationship direction confusion:
 * - If A is parent of B, don't allow B to be parent of A
 * - Ensures parent-child relationships have correct directionality
 *
 * This is actually covered by the cycle detection rule, but we keep it
 * as a separate rule for clearer error messaging
 */
export function validateRelationshipDirection(
  mutation: FamilyTreeMutation,
  context: FamilyTreeGraphContext,
  rules: ProductRules
): ValidationViolation | null {
  let parentId: string;
  let childId: string;

  if (mutation.type === 'add_parent') {
    parentId = mutation.targetPersonId;
    childId = mutation.sourcePersonId;
  } else if (mutation.type === 'add_child') {
    parentId = mutation.sourcePersonId;
    childId = mutation.targetPersonId;
  } else {
    return null; // Rule doesn't apply to spouse mutations
  }

  // Check if the reverse relationship exists
  const reverseExists = GraphInspector.parentChildRelationshipExists(context, childId, parentId);

  if (reverseExists) {
    const parent = GraphInspector.getPerson(context, parentId);
    const child = GraphInspector.getPerson(context, childId);

    return {
      code: 'INVALID_RELATIONSHIP_DIRECTION',
      severity: 'error',
      message: `Invalid relationship direction: ${child?.first_name || 'This person'} is already a parent of ${parent?.first_name || 'this person'}. Cannot reverse parent-child relationships.`,
      details: {
        parentId,
        childId,
      },
    };
  }

  return null;
}

// ==================== RULE 7: INCOMPATIBLE MUTATION WARNINGS ====================

/**
 * Rule: Warn about potentially problematic mutations
 *
 * This rule provides warnings (not errors) for mutations that are technically
 * valid but might indicate user confusion or data quality issues.
 *
 * Examples:
 * - Adding a spouse who shares common ancestors (potential consanguinity)
 * - Adding too many relationships for one person
 */
export function validateMutationCompatibility(
  mutation: FamilyTreeMutation,
  context: FamilyTreeGraphContext,
  rules: ProductRules
): ValidationViolation | null {
  // Check for consanguinity (spouse shares ancestors)
  if (mutation.type === 'add_spouse') {
    const shareAncestors = GraphInspector.shareCommonAncestors(
      context,
      mutation.sourcePersonId,
      mutation.targetPersonId
    );

    if (shareAncestors) {
      const person1 = GraphInspector.getPerson(context, mutation.sourcePersonId);
      const person2 = GraphInspector.getPerson(context, mutation.targetPersonId);

      return {
        code: 'INCOMPATIBLE_MUTATION',
        severity: 'warning',
        message: `${person1?.first_name || 'This person'} and ${person2?.first_name || 'this person'} share common ancestors. Are you sure they should be married?`,
        details: {
          sourcePersonId: mutation.sourcePersonId,
          targetPersonId: mutation.targetPersonId,
          reason: 'consanguinity',
        },
      };
    }
  }

  return null;
}

// ==================== RULE 8: SIBLING CREATION VALIDATION ====================

/**
 * Rule: Validate sibling creation requirements
 *
 * Ensures that siblings can only be created when the source person has at least one parent.
 * This enforces the shared-parents logic for sibling relationships.
 *
 * Applies only to 'add_sibling' mutations
 */
export function validateSiblingCreation(
  mutation: FamilyTreeMutation,
  context: FamilyTreeGraphContext,
  rules: ProductRules
): ValidationViolation | null {
  if (mutation.type !== 'add_sibling') {
    return null; // Rule only applies to add_sibling mutations
  }

  const sourcePersonId = mutation.sourcePersonId;
  const sourceParents = GraphInspector.getParents(context, sourcePersonId);

  // Check if source person has at least one parent
  if (sourceParents.length === 0) {
    const sourcePerson = GraphInspector.getPerson(context, sourcePersonId);

    return {
      code: 'NO_PARENTS_FOR_SIBLING',
      severity: 'error',
      message: `Cannot add sibling: ${sourcePerson?.first_name || 'This person'} has no known parents. Please add at least one parent first before adding siblings.`,
      details: {
        sourcePersonId,
        requiredParents: 1,
        currentParents: 0,
      },
    };
  }

  return null;
}

// ==================== PERSON EXISTENCE VALIDATION ====================

/**
 * Prerequisite Rule: Validate that all referenced persons exist
 *
 * This is a foundational check that should run before other rules
 */
export function validatePersonsExist(
  mutation: FamilyTreeMutation,
  context: FamilyTreeGraphContext,
  rules: ProductRules
): ValidationViolation | null {
  // Check source person
  if (!GraphInspector.personExists(context, mutation.sourcePersonId)) {
    return {
      code: 'PERSON_NOT_FOUND',
      severity: 'error',
      message: 'Source person not found in the family tree.',
      details: {
        personId: mutation.sourcePersonId,
        role: 'source',
      },
    };
  }

  // Check target person (if they already exist - new persons won't exist yet)
  // This check is only relevant for linking existing persons
  // For creating new persons, we skip this check
  if (GraphInspector.personExists(context, mutation.targetPersonId)) {
    // Target exists - we're linking existing persons
    // This is valid, continue
  } else {
    // Target doesn't exist - assume we're creating a new person
    // This is also valid, but we return null since we can't validate further
    // The calling code should handle new person creation
  }

  return null;
}

// ==================== RULE REGISTRY ====================

/**
 * All validation rules in execution order
 * Rules are executed sequentially, stopping at the first violation
 */
export const ALL_VALIDATION_RULES = [
  { name: 'Person Existence', fn: validatePersonsExist },
  { name: 'No Self-Relationship', fn: validateNoSelfRelationship },
  { name: 'No Duplicate Spouse', fn: validateNoDuplicateSpouse },
  { name: 'No Duplicate Parent-Child', fn: validateNoDuplicateParentChild },
  { name: 'Valid Parent Count', fn: validateParentCount },
  { name: 'No Ancestry Cycle', fn: validateNoAncestryCycle },
  { name: 'Valid Relationship Direction', fn: validateRelationshipDirection },
  { name: 'Sibling Creation', fn: validateSiblingCreation },
  { name: 'Mutation Compatibility', fn: validateMutationCompatibility },
] as const;
